import React, { useCallback } from "react";
import { AspectRatio, Box, Image, Text } from "@bookingcom/bui-react";
import { t } from "@bookingcom/lingojs-core";
import { useI18n } from "@bookingcom/lingojs-react";
import { useFormatDateTime } from "@bookingcom/flights-core";

import { SPACER_PADDED } from "@flights/constants";
import { UIPopularFlightComponent, UIPopularFlightInfo } from "@flights/types";
import { getRoundtripSearchUrl, getOnewaySearchUrl } from "../../../utils/search-urls";
import { getMarketingTrackingVariable, isFeatureRunningClientSide } from "@flights/web-api-utils-universal";
import { getCurrencySymbolByPointOfSale } from "@flights/content-universal";
import styles from "./PopularFlightsItem.module.css";

import useGlobalContext from "../../../hooks/useGlobalContext";
import useFormatPrice from "../../../hooks/useFormatPrice";
import useCreateUrl from "../../../hooks/useCreateUrl";
import useTrackFlightsWebUseOfferPos from "../../../hooks/useTrackFlightsWebUseOfferPosExp";
import useLandingPageEvents from "../../../hooks/c360/useLandingPageEvents";
import { getFirstDepartureDate, getLastArrivalDate, getSearchType } from "../../../store/searchBoxState/selectors";
import { useSelector } from "react-redux";
import {
  flights_web_cat_popular_flight_sb_dates_mdot,
  flights_web_cat_popular_flight_sb_dates_www,
  usePopularFlightsDateTracking
} from "../../../utils/experiments/customer-aquisition/flights_web_cat_popular_flight_sb_dates";
import { useLocation } from "react-router-dom";

interface Props {
  flight: UIPopularFlightInfo;
  index: number;
  component: UIPopularFlightComponent;
  isMobile?: boolean;
}

interface Image {
  src: string;
  alt: string;
}

interface SearchData {
  departureDate: string;
  returnDate?: string;
  type: "ONEWAY" | "ROUNDTRIP" | "MULTISTOP";
  popular?: boolean;
  datesChanged?: boolean;
}

const getImage = (toCityImageUrl: string, toCityName: string): Image => {
  return {
    src: toCityImageUrl,
    alt: toCityName
  };
};

const getSearchUrl = (flight: UIPopularFlightInfo, prevSearchParams?: string): string => {
  return getRoundtripSearchUrl(
    {
      fromIata: flight.fromCityIata,
      toIata: flight.toCityIata,
      departureDate: flight.tripStartDate,
      returnDate: flight.tripEndDate
    },
    "flights_index_pf",
    prevSearchParams
  );
};

const getSearchUrlWithSearchData = (
  flight: UIPopularFlightInfo,
  search: SearchData,
  prevSearchParams?: string
): string => {
  const searchUrlModel = {
    fromIata: flight.fromCityIata,
    toIata: flight.toCityIata,
    departureDate: search.departureDate,
    returnDate: search.returnDate,
    popular: search.popular,
    datesChanged: search.datesChanged
  };

  return search.type === "ONEWAY"
    ? getOnewaySearchUrl(searchUrlModel, "flights_index_pf", prevSearchParams)
    : getRoundtripSearchUrl(searchUrlModel, "flights_index_pf", prevSearchParams);
};

const FlightsNearbyItem: React.FunctionComponent<Props> = ({ flight, index, component, isMobile }) => {
  const i18n = useI18n();
  const { formats } = useFormatDateTime(i18n);
  const { ipCountry } = useGlobalContext();
  const flightsWebUseOfferPosExp = useTrackFlightsWebUseOfferPos();
  const searchType = useSelector(getSearchType);
  const firstDepartureDate = useSelector(getFirstDepartureDate);
  const lastArrivalDate = useSelector(getLastArrivalDate);
  const metaSalesCountry = flightsWebUseOfferPosExp.isVariant && getMarketingTrackingVariable("salesCountry");
  const { createUrlWithApiPrefix } = useCreateUrl();
  const { formatPrice } = useFormatPrice();
  const trackLandingPageEvents = useLandingPageEvents();
  const { datesChanged, trackPopularCardClick } = usePopularFlightsDateTracking();
  const location = useLocation();

  const routeName = i18n.trans(
    t("seo_flights_new_index_lp_cheap_flights_meta_flight_route", {
      variables: { city_name_origin: flight.fromCityNameLocalised, city_name_destination: flight.toCityNameLocalised }
    })
  );

  const fromDateFormatted = formats.flightDate(flight.tripStartDate);
  const toDateFormatted = formats.flightDate(flight.tripEndDate);

  const popularFlightsDatesExperimentVariant = isMobile
    ? flights_web_cat_popular_flight_sb_dates_mdot.trackWithDefaultStage()
    : flights_web_cat_popular_flight_sb_dates_www.trackWithDefaultStage();

  const flightParams =
    `${fromDateFormatted} - ${toDateFormatted}` +
    SPACER_PADDED +
    `${i18n.trans(t("seo_flights_new_index_lp_cheap_flights_meta_round_trip"))}`;

  // Optionally show price information.
  const currency = getCurrencySymbolByPointOfSale(
    // @ts-expect-error: Element implicitly has an 'any' type. Fix the issue timely.
    (metaSalesCountry || ipCountry).toLowerCase() as string,
    isFeatureRunningClientSide("FLIGHTS_WEB_CAT_BG_EUR_CURRENCY")
  );
  const fromPriceFormated = flight.price?.units
    ? formatPrice({ currencyCode: currency, units: flight.price?.units, nanos: 0 })
    : "";
  const getFromPrice = () =>
    `${i18n.trans(
      t("seo_flights_new_index_lp_cheap_flights_meta_flight_from_price", {
        variables: { flight_price: fromPriceFormated, num_exception: 1 }
      })
    )}`;

  const urlData = popularFlightsDatesExperimentVariant
    ? getSearchUrlWithSearchData(
        flight,
        {
          type: searchType || "ROUNDTRIP",
          departureDate: firstDepartureDate || new Date().toISOString(),
          returnDate: lastArrivalDate,
          popular: true,
          datesChanged: datesChanged && popularFlightsDatesExperimentVariant === 2
        },
        location?.search
      )
    : getSearchUrl(flight, location?.search);

  const url = createUrlWithApiPrefix(urlData);
  const handleClick = useCallback(() => {
    trackLandingPageEvents("click", component, { ...flight, position: index + 1, url });
    trackPopularCardClick();
  }, [trackLandingPageEvents, component, flight, index, url, trackPopularCardClick]);

  const imageUrl = flight.toCityImageUrl;

  return (
    <a className={styles.anchorContainer} href={url} rel="nofollow" onClick={handleClick} onAuxClick={handleClick}>
      <Box padding={0}>
        <AspectRatio ratio="4:3">
          <Image className={styles.image} {...getImage(imageUrl || "", routeName)} fallback="image" />
        </AspectRatio>
        <div className={styles.textContainer}>
          <Text className={styles.textLine} variant="strong_1">
            {routeName}
          </Text>
          {!popularFlightsDatesExperimentVariant && (
            <Text className={styles.textLine} variant="body_2" color="neutral_alt">
              {flightParams}
            </Text>
          )}
          {fromPriceFormated && (
            <Text className={styles.textLine} variant="body_2" color="neutral_alt">
              {getFromPrice()}
            </Text>
          )}
        </div>
      </Box>
    </a>
  );
};
export default FlightsNearbyItem;
